import { z } from 'zod';
import { ChatMessage, ReasoningEffort, Segment, SpeakerMap, StateDocKind, TurnStatus } from './domain.js';

// The ContextBuilder seam. Shared by @jabberr/context and @jabberr/api.

export const VersionedText = z.object({
  version: z.number().int().nonnegative(),
  text: z.string(),
});
export type VersionedText = z.infer<typeof VersionedText>;

export const HistoryPart = z.object({
  kind: z.enum(['text', 'reasoning', 'tool_call', 'tool_result', 'attachment_ref']),
  content: z.record(z.string(), z.unknown()),
});
export type HistoryPart = z.infer<typeof HistoryPart>;

export const HistoryTurn = z.object({
  kind: z.enum(['user', 'assistant']),
  status: TurnStatus,
  parts: z.array(HistoryPart),
  deliveredText: z.string().optional(),
  /** Conversation sequence number; budgeting and checkpoints need it. */
  seq: z.number().int().optional(),
  /** Group threads (OPEN-23): display name of the human who made a user turn. Rendered as a "Name: " prefix
   *  on the message text so the model sees who said what. Absent in solo threads, where history bytes stay
   *  exactly as they were. */
  speakerName: z.string().optional(),
});
export type HistoryTurn = z.infer<typeof HistoryTurn>;

/** Rolling summary that stands in for every history turn with seq <= throughSeq. */
export const ContextCheckpoint = z.object({
  text: z.string(),
  throughSeq: z.number().int().nonnegative(),
});
export type ContextCheckpoint = z.infer<typeof ContextCheckpoint>;

export const ContextTranscript = z.object({
  segments: z.array(Segment),
  speakerMap: SpeakerMap,
  title: z.string(),
});
export type ContextTranscript = z.infer<typeof ContextTranscript>;

export const ContextDynamic = z.object({
  isoDate: z.string(),
  userName: z.string(),
  speakerMap: SpeakerMap.optional(),
  /** One-line notices that are true now and were not at creation (a state document moved on). */
  notes: z.array(z.string()).optional(),
});
export type ContextDynamic = z.infer<typeof ContextDynamic>;

/** A state document at the version the conversation pinned. `date` is that version's publish date (yyyy-mm-dd). */
export const ContextStateDoc = z.object({
  kind: StateDocKind,
  version: z.number().int().positive(),
  date: z.string(),
  text: z.string(),
});
export type ContextStateDoc = z.infer<typeof ContextStateDoc>;

export const ContextInput = z.object({
  /** The role prompt at the pinned version (prompts/global.md for the product owner). */
  globalPrompt: VersionedText,
  projectContext: VersionedText,
  /** Pinned state documents, already filtered for the role's audience. Rendered in the order given. */
  stateDocs: z.array(ContextStateDoc).optional(),
  /** Snapshots taken at creation and stored on the conversation row, so the prefix never moves. */
  projectMemory: z.string().optional(),
  userMemory: z.string().optional(),
  /** Web search on: the tools section is appended to the prefix and the tool definitions go on every request. */
  webSearch: z.boolean().optional(),
  transcript: ContextTranscript.optional(),
  history: z.array(HistoryTurn),
  /** Rendered as a fixed user/assistant pair right after the system message; never part of the prefix hash. */
  checkpoint: ContextCheckpoint.optional(),
  dynamic: ContextDynamic,
  reasoningEffort: ReasoningEffort,
  enableThinking: z.boolean(),
  padToPageTokens: z.number().int().positive().optional(),
});
export type ContextInput = z.infer<typeof ContextInput>;

export const BuiltContext = z.object({
  messages: z.array(ChatMessage),
  prefixHash: z.string(),
  prefixMessageCount: z.number().int().nonnegative(),
  estimatedPromptTokens: z.number().int().nonnegative(),
  estimatedPrefixTokens: z.number().int().nonnegative(),
  contextVersion: z.number().int().nonnegative(),
});
export type BuiltContext = z.infer<typeof BuiltContext>;

// The recipe's cache page size in tokens (verified in the brief). Padding is off by default.
export const PAGE_TOKENS = 3584;
